import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useAuthToken from '../../hooks/useAuthToken';
import { searchFeatureAccess, updateFeatureAccess } from '../../util/api';
import FeatureAccessGrid from './FeatureAccessGrid';
import {
  EXPAND_BEHAVIORS,
  SearchInput,
  PrimaryButton,
  SecondaryButton,
} from 'cdk-radial';
import { useIamUserProfileStore } from '../../Store';
import getCdkSidFromOkta from '../../util/getCdkSidFromOkta';
import { SPOKE_USERS } from '../../constants';
import {
  Toast,
  TOAST_POSITIONS,
  TOAST_VISIBILITY_DURATIONS,
  BUTTON_VARIANTS,
  BUTTON_ICON_COLORS,
  IconCancelOutlined,
  Checkbox,
} from 'cdk-radial';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import moment from 'moment';

export function dateValueFormatter(value: any) {
    return value ? moment.utc(
          value,
        'dddd, MMMM DD, YYYY at h:mm:ss A Z'
    ).local().format('MM/DD/YYYY h:mm A') : "";

};

export default function FeatureAccessContainer(): JSX.Element {
  const token = useAuthToken();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();

  useEffect(() => {
    trackEvent(AMPLITUDE_EVENTS.DEALER_ADMINISTRATION, { Action: 'View Feature Access' });
    // eslint-disable-next-line
  }, []);
  const [filterValue, setFilterValue] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchType,setSearchType] = useState('');
  const [oldValue, setOldValue] = useState([]);

  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState('');
  const [toastMsg, setToastMsg] = useState('');

  const [meetMyCdkTeamChecked, setMeetMyCdkTeamChecked] = useState(false);

  const [saveDisable, setSaveDisable] = useState(true);

  const iamUserInfo = useIamUserProfileStore((state: any) => state.iamUserInfo);
  const cdksid = getCdkSidFromOkta();
  const enterpriseId = iamUserInfo?.fullUser
    ? iamUserInfo?.fullUser?.enterprise?.id
    : !cdksid
    ? SPOKE_USERS
    : null;

  const toolbar = [
    {
      component: () => (
        <PrimaryButton
          dataTestId="save-button"
          iconColor="primary"
          onClick={handleSave}
          text={t('dealer-admin.feature-access.saveButton')}
          isDisabled={saveDisable}
        />
      ),
    },
  ];

  const columns = [
    {
      header: t('dealer-admin.feature-access.colLastName'),
      field: 'lastName',
      sortable: true,
    },
    {
      header: t('dealer-admin.feature-access.colFirstName'),
      field: 'firstName',
      sortable: true,
    },
    {
      header: t('dealer-admin.feature-access.colCdkSimpleId'),
      field: 'loginId',
      sortable: true,
    },
    {
      columnRenderer: (row: any, index: any) => (
        <>{dateValueFormatter(row.lastUpdatedDate)}</>
      ),
      field: 'lastUpdatedDate',
      header: t('dealer-admin.feature-access.colLastUpdatedDate'),
    },
    {
      header: t('dealer-admin.feature-access.colLastUpdatedBy'),
      field: 'lastUpdatedBy',
      sortable: true,
    },
    {
      field: 'myCdkTeamAccess',
      headerRenderer: (
        <Checkbox
          dataTestId="checkbox"
          id="selectAllMeetMyCdkTeam"
          label={t('dealer-admin.feature-access.colMyCdkTeam')}
          checked={meetMyCdkTeamChecked}
          labelPosition="after"
          name="myCdkTeam"
          onChange={(e: any) => handleSelectOrDeselectAll(e.target.checked)}
          size="standard"
        />
      ),
      columnRenderer: (row: any, index: any) => (
        <Checkbox
          dataTestId={`checkbox-${index}`}
          name={`myteam-${index}`}
          checked={
            row.myCdkTeamAccess === true || row.isEnterpriseAdmin === true
          }
          isDisabled={row.isEnterpriseAdmin === true}
          onChange={(e: any) => handleChange(e, row.loginId, 'myCdkTeamAccess')}
          size="standard"
        />
      ),
    },
  ];

  const search = async () => {
    trackEvent(AMPLITUDE_EVENTS.DEALER_ADMINISTRATION, { Action: 'Search' });
    setMeetMyCdkTeamChecked(false);
    setSaveDisable(true);
    if (searchValue.length < 3) {
      setToastMsg(t('dealer-admin.feature-access.toastMinChars') as string);
      setToastType('Error');
      setShowToast(true);
      return;
    }
    setIsLoading(true);
    setFilterValue([]);
    let users = await searchFeatureAccess(token, enterpriseId, searchValue);
    setFilterValue(users);
    setOldValue(users);
    setIsLoading(false);
    setSaveDisable(users && users.length <= 0);
  };

  const getAllUsers = async () => {
    trackEvent(AMPLITUDE_EVENTS.DEALER_ADMINISTRATION, {
      Action: 'View All Users',
    });
    setMeetMyCdkTeamChecked(false);
    setSaveDisable(true);
    setSearchValue('');
    setIsLoading(true);
    setFilterValue([]);
    let users = await searchFeatureAccess(token, enterpriseId, '');
    setFilterValue(users);
    setOldValue(users);
    setIsLoading(false);
    setSaveDisable(users && users.length <= 0);
  };

  const findUpdatedModel = (arr1: any, arr2: any) => {
    return arr1.filter(
      (item1: any, index: number) =>
        item1['myCdkTeamAccess'] !== arr2[index]['myCdkTeamAccess']
    );
  };

  const save = async () => {
    trackEvent(AMPLITUDE_EVENTS.DEALER_ADMINISTRATION, { Action: 'Save' });

    const updatedModel = findUpdatedModel(filterValue, oldValue);

    if (updatedModel && updatedModel.length === 0) {
      setToastMsg(t('dealer-admin.feature-access.toastNothingToSave') as string);
      setToastType('Error');
      setShowToast(true);
      return;
    }

    const model = updatedModel
      .filter((user: any) => user.myCdkTeamAccess !== undefined)
      .map((user: any) => ({
        loginId: user.loginId.toLowerCase(),
        featureAccess: [
          {
            name: 'MyCdkTeam',
            isAccess: user.myCdkTeamAccess,
          },
        ],
      }));

    try {
      await updateFeatureAccess(model, token);
      setToastMsg(t('dealer-admin.feature-access.toastUpdateSuccess') as string);
      setToastType('positive');
      setShowToast(true);
      if (searchType === 'search') {
        await search();
      } else {
        await getAllUsers();
      }
    } catch (e) {
      setToastMsg(t('dealer-admin.feature-access.toastUpdateError') as string);
      setToastType('negative');
      setShowToast(true);
    }
  };

  const handleChange = (e: any, id: any, columnKey: any) => {
    const newData = filterValue.map((row: any) =>
      row.loginId === id ? { ...row, [columnKey]: e.target.checked } : row
    );
    setFilterValue(newData as []);
  };

  const handleSearchValue = (searchInputValue: any) => {
    setSearchValue(searchInputValue);
  };

  const handleSearch = async () => {
    setSearchType('search');
    await search();
  };

  const handleViewAllUsers = async () => {
    setSearchType('searchAll');
    await getAllUsers();
  };

  const handleSave = async () => {
    setSaveDisable(true);
    await save();
    setSaveDisable(false);
  };

  const resetToastState = () => {
    setToastMsg('');
    setToastType('');
    setShowToast(false);
  };

  const handleSelectOrDeselectAll = (selectOrDeselect: boolean) => {
    setMeetMyCdkTeamChecked(selectOrDeselect);
    const newData = filterValue.map((row: any) => ({
      ...row,
      myCdkTeamAccess: selectOrDeselect,
    }));
    setFilterValue(newData as []);
  };

  return (
    <>
      <div style={{ display: 'flex', margin: '10px' }}>
        <div style={{ width: '530px' }}>
          <SearchInput
            expandBehavior={EXPAND_BEHAVIORS.SLIDE_LEFT}
            id="search-input"
            name="search-input"
            placeholder={t('dealer-admin.feature-access.searchPlaceholder')}
            onChange={handleSearchValue}
            value={searchValue}
            dataTestId="search-input"
            isAlwaysExpanded
          />
        </div>
        <div style={{ marginLeft: '20px' }}>
          <PrimaryButton
            dataTestId="search-button"
            iconColor="primary"
            onClick={handleSearch}
            text={t('dealer-admin.feature-access.searchButton')}
          />
        </div>
        <div style={{ marginLeft: '20px', marginTop: '10px' }}>
          <span>OR </span>
        </div>
        <div style={{ marginLeft: '20px' }}>
          <SecondaryButton
            dataTestId="search-all-button"
            iconColor="secondary"
            onClick={handleViewAllUsers}
            text={t('dealer-admin.feature-access.viewAllUsers')}
          />
        </div>
      </div>

      <FeatureAccessGrid
        rowData={filterValue}
        isLoading={isLoading}
        columns={columns}
        toolbar={toolbar}
      ></FeatureAccessGrid>

      {showToast && (
        <Toast
          id="outage-notification-toast"
          datatestid="outage-notification-toast"
          position={TOAST_POSITIONS.FIXED}
          style={{
            marginTop: '70px',
            zIndex: 1003,
          }}
          variant={toastType === 'Error' ? 'negative' : 'positive'}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          onVisibilityDurationEnd={() => {
            resetToastState();
          }}
          actions={[
            {
              onClick: () => {
                resetToastState();
              },
              variant: BUTTON_VARIANTS.ICON,
              icon: <IconCancelOutlined />,
              iconColor: BUTTON_ICON_COLORS.GRAY,
              text: '',
            },
          ]}
          content={toastMsg}
        />
      )}
    </>
  );
}
